import { useState, useEffect } from 'react';
import PolicyModal from './PolicyModal';

const PRIVACY_POLICY_URL = 'https://financor.pt/politica-de-privacidade/';

interface FooterProps {
  scrollToTop: () => void;
  reloadPage: () => void;
}

interface FooterData {
  logo_image: string;
  address_line1: string;
  address_line2: string;
  email: string;
  facebook_url: string;
  instagram_url: string;
  moacor_url: string;
  moacor_logo: string;
  copyright_text: string;
  privacy_policy_url: string;
  cookies_policy_url: string;
}

interface PoliciesData {
  privacy_policy_pt_text: string;
  cookies_pt_text: string;
}

export default function Footer({ reloadPage }: FooterProps) {
  const [data, setData] = useState<FooterData>({
    logo_image: '/Imagens/LOGO_MULATA.png',
    address_line1: 'Estrada Regional Ribeira Grande, 109',
    address_line2: '9500-702 PONTA DELGADA',
    email: 'bolacha@mulata.pt',
    facebook_url: 'https://www.facebook.com/bolacha.mulata',
    instagram_url: 'https://www.instagram.com/bolacha.mulata/',
    moacor_url: 'https://financor.pt/empresa/moacor/',
    moacor_logo: '/Imagens/LOGO_MOACOR.png',
    copyright_text: '©2026 FINANÇOR. TODOS OS DIREITOS RESERVADOS.',
    privacy_policy_url: '#',
    cookies_policy_url: '#'
  });

  const [policies, setPolicies] = useState<PoliciesData>({
    privacy_policy_pt_text: '',
    cookies_pt_text: ''
  });

  const [activeModal, setActiveModal] = useState<'cookies' | 'copyright' | null>(null);

  useEffect(() => {
    fetchData();
    fetchPolicies();
  }, []);

  const fetchData = async () => {
    try {
      const response = await fetch('https://mulata.wortik.com/bolacha-mulata/api/footer.php');
      const result = await response.json();
      if (result) {
        setData(result);
      }
    } catch (err) {
      console.error('Error loading footer data:', err);
    }
  };

  const fetchPolicies = async () => {
    try {
      const response = await fetch('https://mulata.wortik.com/bolacha-mulata/api/policies.php');
      const result = await response.json();
      if (result) {
        setPolicies({
          privacy_policy_pt_text: result.privacy_policy_pt_text || '',
          cookies_pt_text: result.cookies_pt_text || ''
        });
      }
    } catch (err) {
      console.error('Error loading policies data:', err);
    }
  };

  const getImageSrc = (path: string) => {
    if (path.startsWith('http')) {
      return path;
    }
    return path;
  };

  const getModalTitle = () => {
    switch (activeModal) {
      case 'cookies': return 'Política de Cookies';
      case 'copyright': return data.copyright_text;
      default: return '';
    }
  };

  const getModalContent = () => {
    switch (activeModal) {
      case 'cookies': return policies.cookies_pt_text;
      case 'copyright': return `<p style="text-align: center; font-size: 16px;">${data.copyright_text}</p>`;
      default: return '';
    }
  };

  const handleLinkClick = (e: React.MouseEvent, type: 'cookies' | 'copyright') => {
    e.preventDefault();
    setActiveModal(type);
  };

  return (
    <>
      {/* Modal */}
      <PolicyModal
        isOpen={activeModal !== null}
        onClose={() => setActiveModal(null)}
        title={getModalTitle()}
        content={getModalContent()}
      />

      {/* Footer Section - Desktop */}
      <section 
        className="footer-section-desktop relative" 
        style={{ 
          backgroundImage: 'url(https://i.imgur.com/B3sV2vz.png)',
          backgroundSize: 'cover',
          backgroundPosition: 'center top',
          backgroundRepeat: 'no-repeat',
          width: '100%',
          paddingBottom: '57.29%',
          position: 'relative',
          height: 0,
          imageRendering: '-webkit-optimize-contrast',
          overflow: 'visible',
          zIndex: 1
        }}
      >
        <div 
          className="absolute left-0 right-0 flex flex-col items-center"
          style={{ 
            top: '60%',
            width: '72.08vw',
            margin: '0 auto',
            zIndex: 2
          }}
        >
          <div 
            className="w-full flex items-start justify-between"
          >
            <div className="flex flex-col items-start gap-4">
              <a
                href="/"
                onClick={(e) => {
                  e.preventDefault();
                  reloadPage();
                }}
                className="cursor-pointer hover:scale-105 transition-transform duration-300"
              >
                <img
                  src={getImageSrc(data.logo_image)}
                  alt="Bolacha Mulata"
                  style={{
                    height: '6.67vw',
                    width: 'auto'
                  }}
                  onError={(e) => {
                    e.currentTarget.src = '/Imagens/LOGO_MULATA.png';
                  }}
                />
              </a>
              <div 
                className="text-white prompt-bold-font"
                style={{
                  fontSize: '1.04vw'
                }}
              >
                <p>{data.address_line1}</p>
                <p>{data.address_line2}</p>
              </div>
            </div>

            <div 
              className="flex flex-col items-end"
              style={{ marginTop: '2.08vw' }}
            >
              {/* Email Button */}
              <div className="relative" style={{ marginBottom: '2vw' }}>
                <img
                  src="/Imagens/BOTAO_EMAIL.png"
                  alt="Email Background"
                  style={{
                    height: '3.5vw',
                    width: 'auto'
                  }}
                />
                <a
                  href={`mailto:${data.email}`}
                  className="absolute inset-0 flex items-center justify-center text-white prompt-bold-font hover:scale-105 transition-transform duration-300"
                  style={{
                    fontSize: '1.52vw'
                  }}
                >
                  {data.email}
                </a>
              </div>

              {/* Social Icons */}
              <div 
                className="flex items-center"
                style={{
                  gap: '0.83vw',
                  marginBottom: '2vw'
                }}
              >
                <a
                  href={data.facebook_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:scale-110 transition-transform duration-300"
                >
                  <img
                    src="/Imagens/FB_ICON.png"
                    alt="Facebook"
                    style={{
                      height: '2.5vw',
                      width: 'auto'
                    }}
                  />
                </a>
                <a
                  href={data.instagram_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:scale-110 transition-transform duration-300"
                >
                  <img
                    src="/Imagens/IG_ICON.png"
                    alt="Instagram"
                    style={{
                      height: '2.5vw',
                      width: 'auto'
                    }}
                  />
                </a>
              </div>

              {/* Legal Links - Only first two stacked */}
              <div 
                className="flex flex-col items-end"
                style={{
                  gap: '0.3vw'
                }}
              >
                <a 
                  href="https://www.livroreclamacoes.pt/inicio/" 
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-white prompt-bold-font hover:underline"
                  style={{
                    fontSize: '0.846vw'
                  }}
                >
                  LIVRO DE RECLAMAÇÕES
                </a>
                <a 
                  href={PRIVACY_POLICY_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-white prompt-bold-font hover:underline"
                  style={{
                    fontSize: '0.846vw'
                  }}
                >
                  POLÍTICA DE PRIVACIDADE
                </a>
              </div>
            </div>
          </div>

          <div 
            className="w-full relative"
            style={{ marginTop: '0.1vw' }}
          >
            {/* Bottom row with Copyright and Cookies aligned */}
            <div className="flex items-center justify-between w-full">
              <a 
                href="#"
                onClick={(e) => handleLinkClick(e, 'copyright')}
                className="text-white prompt-bold-font hover:underline cursor-pointer"
                style={{
                  fontSize: '0.846vw'
                }}
              >
                {data.copyright_text}
              </a>
              
              <a 
                href="#"
                onClick={(e) => handleLinkClick(e, 'cookies')}
                className="text-white prompt-bold-font hover:underline cursor-pointer"
                style={{
                  fontSize: '0.846vw'
                }}
              >
                COOKIES
              </a>
            </div>
            
            {/* Moaçor logo centered */}
            <a
              href={data.moacor_url}
              target="_blank"
              rel="noopener noreferrer"
              className="absolute hover:scale-105 transition-transform duration-300"
              style={{
                left: '50%',
                top: '50%',
                transform: 'translate(-50%, -50%)'
              }}
            >
              <img
                src={getImageSrc(data.moacor_logo)}
                alt="Moaçor"
                style={{
                  height: '4.17vw',
                  width: 'auto'
                }}
                onError={(e) => {
                  e.currentTarget.src = '/Imagens/LOGO_MOACOR.png';
                }}
              />
            </a>
          </div>
        </div>
      </section>

      {/* Footer Section - Mobile - 600x920px (153.33% ratio) */}
      <section 
        className="footer-section-mobile relative" 
        style={{ 
          backgroundImage: 'url(https://i.imgur.com/4kAAZYj.png)',
          backgroundSize: 'cover',
          backgroundPosition: 'center top',
          backgroundRepeat: 'no-repeat',
          width: '100%',
          paddingBottom: '153.33%',
          position: 'relative',
          height: 0,
          imageRendering: '-webkit-optimize-contrast',
          overflowX: 'hidden',
          overflowY: 'visible',
          zIndex: 1,
          marginTop: '-5px'
        }}
      >
        {/* Content positioned to match print exactly */}
        <div 
          className="absolute left-0 right-0 flex flex-col items-center"
          style={{
            top: '5%',
            zIndex: 2,
            paddingLeft: '6vw',
            paddingRight: '6vw',
            maxWidth: '100%',
            overflowX: 'hidden',
            boxSizing: 'border-box'
          }}
        >
          {/* Logo */}
          <a
            href="/"
            onClick={(e) => {
              e.preventDefault();
              reloadPage();
            }}
            className="cursor-pointer hover:scale-105 transition-transform duration-300"
          >
            <img
              src={getImageSrc(data.logo_image)}
              alt="Bolacha Mulata"
              style={{
                height: '30vw',
                width: 'auto',
                maxWidth: '100%'
              }}
              onError={(e) => {
                e.currentTarget.src = '/Imagens/LOGO_MULATA.png';
              }}
            />
          </a>

          {/* Email Button */}
          <div 
            className="relative"
            style={{ 
              marginTop: '6vw',
              maxWidth: '88vw'
            }}
          >
            <img
              src="/Imagens/BOTAO_EMAIL.png"
              alt="Email Background"
              style={{
                height: '10.8vw',
                width: 'auto',
                maxWidth: '100%'
              }}
            />
            <a
              href={`mailto:${data.email}`}
              className="absolute inset-0 flex items-center justify-center text-white prompt-bold-font hover:scale-105 transition-transform duration-300"
              style={{
                fontSize: '4.8vw',
                whiteSpace: 'nowrap'
              }}
            >
              {data.email}
            </a>
          </div>

          {/* Social Icons */}
          <div 
            className="flex items-center"
            style={{
              gap: '4vw',
              marginTop: '6vw'
            }}
          >
            <a
              href={data.facebook_url}
              target="_blank"
              rel="noopener noreferrer"
              className="hover:scale-110 transition-transform duration-300"
            >
              <img
                src="/Imagens/FB_ICON.png"
                alt="Facebook"
                style={{
                  height: '9vw',
                  width: 'auto'
                }}
              />
            </a>
            <a
              href={data.instagram_url}
              target="_blank"
              rel="noopener noreferrer"
              className="hover:scale-110 transition-transform duration-300"
            >
              <img
                src="/Imagens/IG_ICON.png"
                alt="Instagram"
                style={{
                  height: '9vw',
                  width: 'auto'
                }}
              />
            </a>
          </div>

          {/* Address */}
          <div 
            className="text-white prompt-bold-font text-center"
            style={{
              fontSize: '4.0vw',
              marginTop: '8vw',
              lineHeight: '1.4',
              maxWidth: '88vw',
              wordBreak: 'break-word',
              overflowWrap: 'break-word'
            }}
          >
            <p>{data.address_line1}</p>
            <p>{data.address_line2}</p>
          </div>

          {/* Moaçor Logo */}
          <a
            href={data.moacor_url}
            target="_blank"
            rel="noopener noreferrer"
            className="hover:scale-105 transition-transform duration-300"
            style={{ marginTop: '8vw' }}
          >
            <img
              src={getImageSrc(data.moacor_logo)}
              alt="Moaçor"
              style={{
                height: '13vw',
                width: 'auto',
                maxWidth: '100%'
              }}
              onError={(e) => {
                e.currentTarget.src = '/Imagens/LOGO_MOACOR.png';
              }}
            />
          </a>

          {/* Legal links stacked vertically and centered */}
          <div 
            className="flex flex-col items-center"
            style={{ 
              marginTop: '8vw',
              gap: '1.5vw',
              maxWidth: '88vw',
              wordBreak: 'break-word',
              overflowWrap: 'break-word'
            }}
          >
            <a 
              href="https://www.livroreclamacoes.pt/inicio/" 
              target="_blank"
              rel="noopener noreferrer"
              className="text-white prompt-bold-font hover:underline text-center"
              style={{
                fontSize: '3.6vw'
              }}
            >
              LIVRO DE RECLAMAÇÕES
            </a>
            <a 
              href={PRIVACY_POLICY_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="text-white prompt-bold-font hover:underline text-center"
              style={{
                fontSize: '3.6vw'
              }}
            >
              POLÍTICA DE PRIVACIDADE
            </a>
            <a 
              href="#"
              onClick={(e) => handleLinkClick(e, 'cookies')}
              className="text-white prompt-bold-font hover:underline text-center cursor-pointer"
              style={{
                fontSize: '3.6vw'
              }}
            >
              COOKIES
            </a>
          </div>

          {/* Copyright */}
          <a 
            href="#"
            onClick={(e) => handleLinkClick(e, 'copyright')}
            className="text-white prompt-bold-font text-center hover:underline cursor-pointer"
            style={{
              fontSize: '2.7vw',
              marginTop: '6vw',
              lineHeight: '1.3',
              maxWidth: '88vw',
              wordBreak: 'break-word',
              overflowWrap: 'break-word'
            }}
          >
            {data.copyright_text}
          </a>
        </div>
      </section>
    </>
  );
}
